import type {SandboxInstance, WatchEvent} from '@blaxel/core';
import {
  SANDBOX_IGNORED_EXTENSIONS,
  SANDBOX_WATCH_IGNORE_DIRECTORIES,
  SANDBOX_WORKING_DIR,
} from '../constants';

/**
 * Reserved for unrecoverable tracking failures. The helper first falls back to
 * a directory snapshot when the watch start barrier is unavailable.
 */
export class SnapshotUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SnapshotUnavailableError';
  }
}

// Markers must live inside the watched root and stay visible to watch backends.
const MARKER_PREFIX = 'wf-tracking-';
const START_MARKER_KIND = 'start';
const END_MARKER_KIND = 'end';

const MARKER_BARRIER_TIMEOUT_MS = 30000;
const MARKER_BARRIER_RETRY_INTERVAL_MS = 500;
const MARKER_CONTENT = '1';

const markerFilename = (kind: string, buildId: string): string =>
  `${MARKER_PREFIX}${kind}-${buildId}`;

const isMarkerName = (name: string): boolean => name.startsWith(MARKER_PREFIX);

const shouldIgnoreEventName = (name: string): boolean =>
  SANDBOX_IGNORED_EXTENSIONS.some((ext) => name.endsWith(ext));

const shouldIgnoreDirectoryName = (name: string): boolean =>
  SANDBOX_WATCH_IGNORE_DIRECTORIES.includes(name);

const joinEventPath = (event: WatchEvent): string =>
  event.path.endsWith('/')
    ? event.path + event.name
    : event.path + '/' + event.name;

const toRelativePath = (fullPath: string): string => {
  const prefix = SANDBOX_WORKING_DIR + '/';
  return fullPath.startsWith(prefix) ? fullPath.slice(prefix.length) : fullPath;
};

type SandboxDirectory = Awaited<ReturnType<SandboxInstance['fs']['ls']>>;
type FileSnapshot = Map<string, string>;
type TrackingMode = 'watch' | 'snapshot_fallback';
type FileChangeTrackingResult<T> = {
  result: T;
  committed: Record<string, string>;
  deleted: string[];
  trackingMode: TrackingMode;
  // Time from opening the watcher until the start barrier settled (resolved,
  // or failed into the snapshot fallback). Unset when watch() threw.
  startBarrierMs?: number;
  // Whether any start-marker write succeeded. Separates a stream that never
  // delivered (written, ~30s) from failing writes (not written, ~30s).
  startMarkerWritten?: boolean;
  // Time from writing the end marker until it was observed. Unset when the
  // end barrier failed.
  endBarrierMs?: number;
};

const directoryPath = (
  directory: SandboxDirectory,
  child: {path?: string; name: string}
): string => {
  if (child.path) return child.path;
  return directory.path.endsWith('/')
    ? directory.path + child.name
    : `${directory.path}/${child.name}`;
};

const listTrackedFilePaths = async (
  sandbox: SandboxInstance,
  path = SANDBOX_WORKING_DIR
): Promise<string[]> => {
  const directory = await sandbox.fs.ls(path);
  const files: string[] = [];

  for (const file of directory.files) {
    if (isMarkerName(file.name)) continue;
    if (shouldIgnoreEventName(file.name)) continue;
    files.push(toRelativePath(directoryPath(directory, file)));
  }

  for (const subdirectory of directory.subdirectories) {
    if (shouldIgnoreDirectoryName(subdirectory.name)) continue;
    files.push(
      ...(await listTrackedFilePaths(
        sandbox,
        directoryPath(directory, subdirectory)
      ))
    );
  }

  return files;
};

const readSnapshot = async (
  sandbox: SandboxInstance
): Promise<FileSnapshot> => {
  const snapshot: FileSnapshot = new Map();
  for (const path of await listTrackedFilePaths(sandbox)) {
    try {
      snapshot.set(
        path,
        await sandbox.fs.read(`${SANDBOX_WORKING_DIR}/${path}`)
      );
    } catch {
      // Transient files can disappear while snapshotting.
    }
  }
  return snapshot;
};

const diffSnapshots = (
  before: FileSnapshot,
  after: FileSnapshot
): {committed: Record<string, string>; deleted: string[]} => {
  const committed: Record<string, string> = {};
  const deleted: string[] = [];

  for (const [path, content] of after) {
    if (before.get(path) !== content) {
      committed[path] = content;
    }
  }

  for (const path of before.keys()) {
    if (!after.has(path)) {
      deleted.push(path);
    }
  }

  return {committed, deleted};
};

const withDirectorySnapshot = async <T>(
  sandbox: SandboxInstance,
  operation: (sandbox: SandboxInstance) => Promise<T>
): Promise<FileChangeTrackingResult<T>> => {
  const before = await readSnapshot(sandbox);
  const result = await operation(sandbox);
  const after = await readSnapshot(sandbox);
  return {
    result,
    ...diffSnapshots(before, after),
    trackingMode: 'snapshot_fallback',
  };
};

const withTimeout = async <T>(
  promise: Promise<T>,
  ms: number,
  label: string
): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () => reject(new Error(`${label} did not arrive within ${ms}ms`)),
          ms
        );
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

const writeMarkerUntilObserved = async ({
  writeMarker,
  barrier,
  label,
}: {
  writeMarker: () => Promise<void>;
  barrier: Promise<void>;
  label: string;
}): Promise<void> => {
  let done = false;
  let lastWriteError: unknown;
  let cancelRetryWait = () => {};
  const waitForRetry = (): Promise<void> =>
    new Promise((resolve) => {
      const timer = setTimeout(() => {
        cancelRetryWait = () => {};
        resolve();
      }, MARKER_BARRIER_RETRY_INTERVAL_MS);
      cancelRetryWait = () => {
        clearTimeout(timer);
        cancelRetryWait = () => {};
        resolve();
      };
    });

  const writeUntilDone = async (): Promise<void> => {
    if (done) return;
    try {
      await writeMarker();
      lastWriteError = undefined;
    } catch (err) {
      lastWriteError = err;
    }
    if (done) return;
    await waitForRetry();
    return writeUntilDone();
  };
  const writer = writeUntilDone();

  try {
    // The SDK returns a watcher handle before the stream is always attached.
    await withTimeout(barrier, MARKER_BARRIER_TIMEOUT_MS, label);
  } catch (err) {
    if (lastWriteError !== undefined) {
      throw lastWriteError;
    }
    throw err;
  } finally {
    done = true;
    cancelRetryWait();
    await writer.catch(() => {});
  }
};

/**
 * Runs `operation` inside a marker-fenced watch window and returns the files
 * changed under `SANDBOX_WORKING_DIR` with their post-op contents.
 *
 * Each invocation opens its own short-lived watcher and writes unique start/end
 * markers inside the watched root. The start marker proves the stream is hot;
 * if it is not observed, the watcher is closed and the operation runs through a
 * before/after directory snapshot fallback. The end marker is best-effort: by
 * then the operation has already run, so a missed end marker warns instead of
 * discarding the build result.
 */
export async function withFileChangeTracking<T>(
  sandbox: SandboxInstance,
  operation: (sandbox: SandboxInstance) => Promise<T>
): Promise<FileChangeTrackingResult<T>> {
  const buildId = generateBuildId();
  const startMarkerName = markerFilename(START_MARKER_KIND, buildId);
  const endMarkerName = markerFilename(END_MARKER_KIND, buildId);
  const startMarkerPath = `${SANDBOX_WORKING_DIR}/${startMarkerName}`;
  const endMarkerPath = `${SANDBOX_WORKING_DIR}/${endMarkerName}`;
  let startMarkerWritten = false;
  let endMarkerWritten = false;

  const committedPaths = new Set<string>();
  const deletedPaths = new Set<string>();

  // Ignore synthetic CREATEs until our marker proves the stream is hot.
  let trackingActive = false;

  let resolveStart: (() => void) | null = null;
  let rejectStart: ((err: Error) => void) | null = null;
  let resolveEnd: (() => void) | null = null;
  const startBarrier = new Promise<void>((resolve, reject) => {
    resolveStart = resolve;
    rejectStart = reject;
  });
  const endBarrier = new Promise<void>((resolve) => {
    resolveEnd = resolve;
  });

  const handleEvent = (event: WatchEvent) => {
    if (event.name === startMarkerName) {
      trackingActive = true;
      resolveStart?.();
      resolveStart = null;
      rejectStart = null;
      return;
    }
    if (event.name === endMarkerName) {
      trackingActive = false;
      resolveEnd?.();
      resolveEnd = null;
      return;
    }
    if (isMarkerName(event.name)) return;
    if (shouldIgnoreEventName(event.name)) return;
    if (!trackingActive) return;

    const relativePath = toRelativePath(joinEventPath(event));
    if (event.op === 'REMOVE' || event.op === 'RENAME') {
      deletedPaths.add(relativePath);
      committedPaths.delete(relativePath);
    } else {
      committedPaths.add(relativePath);
      deletedPaths.delete(relativePath);
    }
  };

  const handleWatcherError = (err: Error) => {
    if (rejectStart) {
      rejectStart(err);
      return;
    }
    console.warn(
      '[withFileChangeTracking] watcher error after start barrier; tracking may be incomplete',
      {message: err.message}
    );
  };

  let watcher: {close: () => void} | null = null;
  let setupError: unknown;
  let result: T | undefined;
  let operationError: unknown;
  let startBarrierMs: number | undefined;
  let endBarrierMs: number | undefined;

  const watchStart = performance.now();
  try {
    watcher = sandbox.fs.watch(`${SANDBOX_WORKING_DIR}/**`, handleEvent, {
      withContent: false,
      ignore: SANDBOX_WATCH_IGNORE_DIRECTORIES,
      onError: handleWatcherError,
    });
  } catch (err) {
    setupError = err;
  }

  try {
    try {
      if (setupError === undefined) {
        await writeMarkerUntilObserved({
          writeMarker: async () => {
            await sandbox.fs.write(startMarkerPath, MARKER_CONTENT);
            startMarkerWritten = true;
          },
          barrier: startBarrier,
          label: 'start marker',
        });
        startBarrierMs = performance.now() - watchStart;
      }
    } catch (err) {
      setupError = err;
      startBarrierMs = performance.now() - watchStart;
    }

    if (setupError === undefined) {
      try {
        result = await operation(sandbox);
      } catch (err) {
        operationError = err;
      }

      try {
        const endStart = performance.now();
        await sandbox.fs.write(endMarkerPath, MARKER_CONTENT);
        endMarkerWritten = true;
        await withTimeout(endBarrier, MARKER_BARRIER_TIMEOUT_MS, 'end marker');
        endBarrierMs = performance.now() - endStart;
      } catch (err) {
        trackingActive = false;
        console.warn(
          '[withFileChangeTracking] end marker barrier failed; tracking may be incomplete',
          {message: err instanceof Error ? err.message : String(err)}
        );
      }
    }
  } finally {
    watcher?.close();
    if (startMarkerWritten) {
      await sandbox.fs.rm(startMarkerPath).catch(() => {});
    }
    if (endMarkerWritten) {
      await sandbox.fs.rm(endMarkerPath).catch(() => {});
    }
  }

  if (setupError !== undefined) {
    console.warn(
      '[withFileChangeTracking] start marker barrier failed; falling back to directory snapshot',
      {
        message:
          setupError instanceof Error ? setupError.message : String(setupError),
      }
    );
    return {
      ...(await withDirectorySnapshot(sandbox, operation)),
      startBarrierMs,
      startMarkerWritten,
    };
  }

  if (operationError !== undefined) {
    throw operationError;
  }

  const committed: Record<string, string> = {};
  for (const path of committedPaths) {
    try {
      committed[path] = await sandbox.fs.read(`${SANDBOX_WORKING_DIR}/${path}`);
    } catch {
      // Transient files can disappear before the post-op read.
    }
  }

  return {
    result: result as T,
    committed,
    deleted: [...deletedPaths],
    trackingMode: 'watch',
    startBarrierMs,
    startMarkerWritten,
    endBarrierMs,
  };
}

const generateBuildId = (): string => {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return globalThis.crypto.randomUUID();
  }
  return (
    Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
  );
};
